import React, { useEffect, useState, useRef, useMemo } from "react";
import { Label } from "@progress/kendo-react-labels";
import { Field } from "@progress/kendo-react-form";
import { filterBy } from "@progress/kendo-data-query";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import * as api from "../../api/index";
import CONSTANT from "../../CONSTANT/Matter/ComboxType";

import { TransactionTypes } from "../../components/Enums/TransactionTypes.ts";
import { NominalSubtypes } from "../../components/Enums/Nominals.ts";
import { getActiveVats } from "services/vatService";
import { getActiveBranches } from "services/branchService";
import { getActiveFeeEarner } from "services/userService";
import { getPostingTypes } from "services/postingTypesService";
import { getActiveSuppliers } from "services/supplierService";
import {
  getActiveIncomeNominals,
  getAllNominals,
} from "services/nominalsService";

const requiredValidator = (value) => (value ? "" : "This field is required.");

const SearchComboBox = ({
  postingType,
  onChangeFunction,
  cbType,
  width = "255px",
  boxShadow = false,
  readOnly,
  name,
  VatCode,
  controlled = false,
  value = "",
  onChange,
  style,
  customCombo,
  visited,
  validationMessage,
  fromValue,
  toValue,
  borderRadius,
  defaultValues,
  NetAmount,
  customLabel,
  defaultValueSetter,
  required,
  customValidation,
  customStyle,
  placeholder,
  error,
  restrictClientBankCurrencyCode,
  onAfterFieldChange,
  autoSelectFirst = false,
  hasAutoSelected,
  setHasAutoSelected,
  deferChangeUntilClose = false,
  ...others
}) => {
  const {
    onOpen: userOnOpen,
    onClose: userOnClose,
    ...comboBoxRest
  } = others || {};
  const styleObject = {
    boxShadow: boxShadow ? "0px 0px 16px rgba(0, 0, 0, 0.1)" : "",
    width: width,
    borderRadius: borderRadius ? borderRadius : null,
    border: error ? "1px solid red" : undefined,
  };
  const [data, setData] = useState([]);
  const [apiData, setApiData] = useState([]);

  const [internalHasAutoSelected, setInternalHasAutoSelected] = useState(false);
  const hasAutoSelectedState =
    hasAutoSelected !== undefined ? hasAutoSelected : internalHasAutoSelected;
  const setHasAutoSelectedState =
    setHasAutoSelected || setInternalHasAutoSelected;
  const loadedForType = useRef(null);
  const pendingValueRef = useRef(value);
  const [isOpen, setIsOpen] = useState(false);
  const [tempValue, setTempValue] = useState(value);

  const getInfo = (cbType) => {
    switch (cbType) {
      case CONSTANT.SR:
        return { textField: "display", label: "Supplier" };
      case CONSTANT.CR:
        return { textField: "display", label: "Consultant" };
      case CONSTANT.NR:
        return { textField: "display", label: "Nominal" };
      case CONSTANT.NB:
        return { textField: "display", label: "Nominal" };
      case CONSTANT.FBT:
        return { textField: "display", label: "From Bank" };
      case CONSTANT.TBT:
        return { textField: "display", label: "To Bank" };
      case CONSTANT.BRANCHES:
        return { textField: "display", label: "Branch" };
      case CONSTANT.FE:
        return { textField: "display", label: "Fee Earner" };
      case CONSTANT.OB:
        return { textField: "display", label: "Office Bank" };
      case CONSTANT.CB:
        return { textField: "display", label: "Client Bank" };
      case CONSTANT.DDAB:
        return { textField: "display", label: "DDA Bank" };
      case CONSTANT.AVATS:
        return { textField: "display", label: "VAT Code" };
      case CONSTANT.MATTER:
        return { textField: "matterRef", label: "Matter" };
      case CONSTANT.POSTING_TYPE:
        return {
          textField: "display",
          dataItemKey: "code",
          label: "Posting Type",
        };
      default:
        return { textField: "display", label: "" };
    }
  };

  const filterData = (filter) => {
    let source = apiData.slice();
    if (cbType === CONSTANT.CB && restrictClientBankCurrencyCode) {
      source = source.filter(
        (n) => n.currencyCode === restrictClientBankCurrencyCode,
      );
    }
    return filterBy(source, filter);
  };

  const filterChange = (event) => {
    setData(filterData(event.filter));
  };

  const onChangeTransfer = (e) => {
    if (cbType.FBT && e.value?.nominalRef === toValue?.nominalRef) {
      return;
    } else if (cbType.TBT && e.value?.nominalRef === fromValue?.nominalRef) {
      return;
    } else {
      onChange && onChange(e);
    }
  };
  const prepareNominalData = (data, type) => {
    let array = [];
    if (type !== null) {
      data?.map((item) => {
        if (item.subType === type) {
          array.push(item);
        }
      });
      return array;
    } else {
      return data || [];
    }
  };

  const prepareSupplierRefsData = (data) => {
    let list = [];
    data.map((item) => {
      if (
        postingType === "PIN" &&
        item.transactionTypes === TransactionTypes.Purchase
      ) {
        item.representation = item.supplierRef + " - " + item.supplierName;
        list.push(item);
        return;
      } else if (
        postingType === "DIN" &&
        item.transactionTypes !== TransactionTypes.Purchase
      ) {
        item.representation = item.supplierRef + " - " + item.supplierName;
        list.push(item);
        return;
      } else {
        item.representation = item.supplierRef + " - " + item.supplierName;
        list.push(item);
        return;
      }
    });
    return list;
  };

  const getData = async (cbType) => {
    try {
      let apiRes;
      if (cbType === CONSTANT.SR) {
        const data = await getActiveSuppliers();
        setApiData(data.slice());
        setData(data.slice());
        return;
      }
      if (cbType === CONSTANT.POSTING_TYPE) {
        const data = await getPostingTypes(postingType);
        setApiData(data.slice());
        setData(data.slice());

        if (
          autoSelectFirst &&
          !hasAutoSelectedState &&
          !value &&
          data &&
          data.length > 0
        ) {
          const firstItem = data[0];
          if (onChangeFunction) {
            onChangeFunction({ value: firstItem });
          }
          setHasAutoSelectedState(true);
        }
        return;
      }
      if (cbType === CONSTANT.BRANCHES) {
        const branchesList = await getActiveBranches();
        setApiData(branchesList || []);
        setData(branchesList || []);
        return;
      }
      if (cbType === CONSTANT.OB) {
        const allNominals = await getAllNominals();
        const filtered =
          allNominals?.filter(
            (item) =>
              item.archived !== true &&
              item.subType === NominalSubtypes.OFFICE_BANK,
          ) || [];
        setApiData(filtered);
        setData(filtered);
        return;
      }
      if (cbType === CONSTANT.CB) {
        const allNominals = await getAllNominals();
        const filtered =
          allNominals?.filter(
            (item) =>
              item.archived !== true &&
              item.subType === NominalSubtypes.CLIENT_BANK,
          ) || [];
        setApiData(filtered);
        return;
      }
      if (cbType === CONSTANT.MATTER) apiRes = await api.getMatters();
      if (cbType === CONSTANT.DDAB) {
        const allNominals = await getAllNominals();
        const filtered =
          allNominals?.filter(
            (item) =>
              item.archived !== true &&
              item.subType === NominalSubtypes.DDA_BANK,
          ) || [];
        setApiData(filtered);
        setData(filtered);
        return;
      }
      if (cbType === CONSTANT.FE) {
        const data = await getActiveFeeEarner();
        setApiData(data || []);
        setData(data || []);

        if (defaultValueSetter) {
          defaultValueSetter?.onChange(name, {
            value: data.find(
              (item) => item.userRef === defaultValues?.matterFeeEarner,
            ),
          });
        }
        return;
      }
      if (cbType === CONSTANT.NR) {
        const allNominals = await getAllNominals();
        const filtered =
          allNominals?.filter(
            (item) => item.archived !== true && item.type === "ProfitAndLoss",
          ) || [];
        setApiData(filtered);
        setData(filtered);
        return;
      }
      if (cbType === CONSTANT.NB) {
        const { data } = await getActiveIncomeNominals();
        const preparedData = prepareNominalData(data.data, null);
        setApiData(preparedData.slice());
        setData(preparedData.slice());
        return;
      }
      if (cbType === CONSTANT.FBT || cbType === CONSTANT.TBT) {
        const allNominals = await getAllNominals();
        const filtered =
          allNominals?.filter(
            (item) =>
              item.archived !== true &&
              item.subType === NominalSubtypes.OFFICE_BANK,
          ) || [];
        setApiData(filtered);
        setData(filtered);
        return;
      }
      if (cbType === CONSTANT.AVATS) {
        const data = await getActiveVats();
        setApiData(data || []);
        setData(data || []);
        return;
      }

      const { data } = apiRes;
      setApiData(data.data.slice());
      setData(data.data.slice());
    } catch (error) {}
  };

  useEffect(() => {
    const currentKey = `${postingType}-${cbType}`;
    if (loadedForType.current !== currentKey) {
      getData(cbType);
      loadedForType.current = currentKey;
    }
  }, [postingType, cbType]);

  useEffect(() => {
    if (cbType !== CONSTANT.CB) return;
    const base = apiData || [];
    const next = restrictClientBankCurrencyCode
      ? base.filter((n) => n.currencyCode === restrictClientBankCurrencyCode)
      : base.slice();
    setData(next);
  }, [apiData, restrictClientBankCurrencyCode, cbType]);

  useEffect(() => {
    if (!deferChangeUntilClose) return;
    pendingValueRef.current = value;
    if (!isOpen) {
      setTempValue(value);
    }
  }, [value, deferChangeUntilClose, isOpen]);

  const afterChangeRef = useRef(onAfterFieldChange);
  afterChangeRef.current = onAfterFieldChange;

  const StableComboField = useMemo(
    () =>
      React.forwardRef((props, ref) => {
        const {
          onChange,
          validationMessage,
          validationAttempted,
          visited,
          touched,
          style,
          ...rest
        } = props;
        const showError =
          Boolean(validationMessage) &&
          Boolean(validationAttempted || visited || touched);
        const mergedStyle = {
          ...(typeof style === "object" && style ? style : {}),
          ...(showError ? { border: "1px solid red" } : {}),
        };
        return (
          <ComboBox
            ref={ref}
            {...rest}
            style={mergedStyle}
            onChange={(e) => {
              onChange(e);
              afterChangeRef.current?.(e);
            }}
          />
        );
      }),
    [],
  );

  return (
    <div className="flex flex-column" style={style}>
      <Label className="label">{customLabel || getInfo(cbType)?.label}</Label>
      {controlled ? (
        <Field
          {...others}
          textField={getInfo(cbType)?.textField}
          dataItemKey={
            getInfo(cbType)?.dataItemKey || getInfo(cbType)?.textField
          }
          disabled={readOnly}
          data={data}
          iconClassName="wa"
          value={value}
          name={name}
          onChange={(e) => {
            if (onChangeFunction) onChangeFunction(e);
          }}
          style={styleObject}
          filterable={true}
          onFilterChange={filterChange}
          component={ComboBox}
        />
      ) : customCombo ? (
        <ComboBox
          {...comboBoxRest}
          textField={getInfo(cbType)?.textField}
          dataItemKey={
            getInfo(cbType)?.dataItemKey || getInfo(cbType)?.textField
          }
          disabled={readOnly}
          data={data}
          iconClassName="wa"
          placeholder={placeholder ? "Select " + getInfo(cbType)?.label : ""}
          value={deferChangeUntilClose && isOpen ? tempValue : value}
          required={required}
          name={name}
          onOpen={(e) => {
            if (deferChangeUntilClose) {
              setIsOpen(true);
              setTempValue(value);
              pendingValueRef.current = value;
            }
            userOnOpen?.(e);
          }}
          onChange={(e) => {
            if (deferChangeUntilClose) {
              if (!isOpen) {
                pendingValueRef.current = e.value;
                setTempValue(e.value);
                onChangeFunction?.(e);
                return;
              }
              pendingValueRef.current = e.value;
              setTempValue(e.value);
              return;
            }
            if (onChangeFunction) onChangeFunction(e);
          }}
          onClose={(e) => {
            if (deferChangeUntilClose) {
              setIsOpen(false);
              if (onChangeFunction) {
                onChangeFunction({
                  ...e,
                  value: pendingValueRef.current,
                });
              }
            }
            userOnClose?.(e);
          }}
          style={styleObject}
          filterable={true}
          onFilterChange={filterChange}
        />
      ) : (
        <Field
          {...others}
          validator={!customValidation && requiredValidator}
          textField={getInfo(cbType)?.textField}
          dataItemKey={
            getInfo(cbType)?.dataItemKey || getInfo(cbType)?.textField
          }
          disabled={readOnly}
          name={name}
          data={data}
          iconClassName="wa"
          placeholder={placeholder}
          style={customStyle ? { ...styleObject, ...customStyle } : styleObject}
          filterable={true}
          onFilterChange={filterChange}
          component={StableComboField}
        />
      )}
    </div>
  );
};

export default SearchComboBox;
